import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaUserTie, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaCheckCircle } from 'react-icons/fa';
import TeacherMultiSelect from './TeacherMultiSelect';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const emptyForm = () => ({
  postingDepartment: '',
  selectedTeacherIds: [],
  noteToFaculty: ''
});

const STATUS_LABEL = { requested: 'Requested', scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled' };
const STATUS_CLASS = { requested: 'ug-status-pending', scheduled: 'ug-status-not-started', completed: 'ug-status-approved', cancelled: 'ug-status-revision' };

const UGProfessionalism = ({ onBack }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchAssessments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/professionalism/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAssessments(data.assessments || []);
    } catch (err) {
      console.error('Error fetching FAP assessments:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      postingDepartment: entry.postingDepartment,
      selectedTeacherIds: (entry.selectedTeacherIds || []).map(t => t._id || t),
      noteToFaculty: entry.noteToFaculty || ''
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.postingDepartment.trim()) {
      alert('Please fill in Posting / Department before submitting.');
      return;
    }
    if (!form.selectedTeacherIds.length) {
      alert('Please select at least one faculty member to grade this request.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/professionalism${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchAssessments();
      } else {
        alert(data.message || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Error saving FAP request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Withdraw this FAP request? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/professionalism/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchAssessments();
      else alert(data.message || 'Failed to withdraw request');
    } catch (err) {
      console.error('Error deleting FAP request:', err);
    }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaUserTie /> Formative Assessment of Professionalism (FAP)</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Professionalism reviews by faculty — five behavioural domains rated on a 1–5 scale, with faculty remarks.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Request assessment
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading FAP assessments...</div>
      ) : assessments.length === 0 ? (
        <div className="ug-due-empty">
          <p style={{ margin: '0 0 4px 0', fontWeight: 700, color: '#1f2430' }}>No FAP assessments yet</p>
          <p style={{ margin: '0 0 14px 0' }}>
            Request a Formative Assessment of Professionalism from your faculty mentor. They will rate your professional behaviour across five domains.
          </p>
          <button className="ug-btn-primary" onClick={openNew}>Request your first assessment</button>
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {assessments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <span className={`ug-status-badge ${STATUS_CLASS[entry.status] || 'ug-status-not-started'}`}>
                  {entry.status === 'completed' && <FaCheckCircle />} {STATUS_LABEL[entry.status] || entry.status}
                </span>
              </div>
              <h4 className="ug-reflection-period">{entry.postingDepartment}</h4>
              <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                {entry.selectedTeacherIds?.length > 0 && (
                  <span>Faculty: {entry.selectedTeacherIds.map(t => `${t.profile?.firstName} ${t.profile?.lastName}`).join(', ')}</span>
                )}
              </div>

              {entry.noteToFaculty && (
                <>
                  <div className="ug-reflection-section-label">Note to Faculty</div>
                  <p className="ug-reflection-text">{entry.noteToFaculty}</p>
                </>
              )}

              {entry.status === 'completed' && entry.assessment && (
                <>
                  {entry.assessment.domainScores?.length > 0 && (
                    <>
                      <div className="ug-reflection-section-label">Domain Scores (1–5)</div>
                      <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                        {entry.assessment.domainScores.map((d, i) => (
                          <span key={i}>{d.domain}: {d.score}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {entry.assessment.facultyRemarks && (
                    <>
                      <div className="ug-reflection-section-label">Faculty Remarks</div>
                      <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.assessment.facultyRemarks}</p>
                    </>
                  )}
                  {entry.assessment.assessedByName && (
                    <p className="ug-reviewer-tag">Assessed by {entry.assessment.assessedByName}</p>
                  )}
                </>
              )}

              {entry.status === 'requested' && (
                <div className="ug-reflection-card-actions">
                  <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                  <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Withdraw"><FaTrash /></button>
                </div>
              )}
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={assessments.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Request FAP assessment</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Posting / Department</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. Medicine OPD, Surgery Ward"
              value={form.postingDepartment}
              onChange={e => setForm({ ...form, postingDepartment: e.target.value })}
            />

            <label className="ug-form-label">Faculty</label>
            <TeacherMultiSelect
              value={form.selectedTeacherIds}
              onChange={selectedTeacherIds => setForm({ ...form, selectedTeacherIds })}
            />

            <label className="ug-form-label">Note to faculty (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.noteToFaculty}
              onChange={e => setForm({ ...form, noteToFaculty: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGProfessionalism;
